// Contenuti SEO specifici per ogni città/provincia italiana
import { italianProvinces, getProvinceByCity, cityExists } from './cities';

export interface LocationSEO {
  cityName: string;
  provinceName?: string;
  regionName: string;
  titleKey: string;
  descriptionKey: string;
  h1Key: string;
  contentKey: string;
  keywordsKey: string;
  locationType: 'city' | 'province' | 'region';
}

export const seoTemplates = {  // Template base per città principali
  cityTemplate: (cityName: string, regionName: string, provinceName?: string): LocationSEO => ({
    cityName,
    provinceName,
    regionName,
    locationType: 'city',
    titleKey: 'seoTemplates.city.title',
    descriptionKey: 'seoTemplates.city.description',
    h1Key: 'seoTemplates.city.h1',
    contentKey: 'seoTemplates.city.content',
    keywordsKey: 'seoTemplates.city.keywords'
  }),
  // Template per province
  provinceTemplate: (provinceName: string, regionName: string): LocationSEO => ({
    cityName: provinceName,
    provinceName,
    regionName,
    locationType: 'province',
    titleKey: 'seoTemplates.province.title',
    descriptionKey: 'seoTemplates.province.description',
    h1Key: 'seoTemplates.province.h1',
    contentKey: 'seoTemplates.province.content',
    keywordsKey: 'seoTemplates.province.keywords'
  }),
  // Template per regioni
  regionTemplate: (regionName: string): LocationSEO => ({
    cityName: regionName,
    regionName,
    locationType: 'region',
    titleKey: 'seoTemplates.region.title',
    descriptionKey: 'seoTemplates.region.description',
    h1Key: 'seoTemplates.region.h1',
    contentKey: 'seoTemplates.region.content',
    keywordsKey: 'seoTemplates.region.keywords'
  })
};

// Dati specifici per le città principali
export const majorCitiesSEO: LocationSEO[] = [
  {
    cityName: 'Roma',
    regionName: 'Lazio',
    locationType: 'city',
    titleKey: 'seoTemplates.cities.roma.title',
    descriptionKey: 'seoTemplates.cities.roma.description',
    h1Key: 'seoTemplates.cities.roma.h1',
    contentKey: 'seoTemplates.cities.roma.content',
    keywordsKey: 'seoTemplates.cities.roma.keywords'
  },  {
    cityName: 'Milano',
    regionName: 'Lombardia',
    locationType: 'city',
    titleKey: 'seoTemplates.cities.milano.title',
    descriptionKey: 'seoTemplates.cities.milano.description',
    h1Key: 'seoTemplates.cities.milano.h1',
    contentKey: 'seoTemplates.cities.milano.content',
    keywordsKey: 'seoTemplates.cities.milano.keywords'
  },  {
    cityName: 'Napoli',
    regionName: 'Campania',
    locationType: 'city',
    titleKey: 'seoTemplates.cities.napoli.title',
    descriptionKey: 'seoTemplates.cities.napoli.description',
    h1Key: 'seoTemplates.cities.napoli.h1',
    contentKey: 'seoTemplates.cities.napoli.content',
    keywordsKey: 'seoTemplates.cities.napoli.keywords'
  },  {
    cityName: 'Torino',
    regionName: 'Piemonte',
    locationType: 'city',
    titleKey: 'seoTemplates.cities.torino.title',
    descriptionKey: 'seoTemplates.cities.torino.description',
    h1Key: 'seoTemplates.cities.torino.h1',
    contentKey: 'seoTemplates.cities.torino.content',
    keywordsKey: 'seoTemplates.cities.torino.keywords'
  }
];

// Funzioni utility per gestire le location
export const findLocationSEO = (locationName: string): LocationSEO | null => {
  const decodedLocation = decodeURIComponent(locationName).toLowerCase();
  
  // Cerca nelle città principali
  let locationSEO = majorCitiesSEO.find(city => 
    city.cityName.toLowerCase() === decodedLocation
  ) || null;
  
  if (locationSEO) return locationSEO;
  
  // Cerca nelle città del database
  if (cityExists(decodedLocation)) {
    return getCitySpecificSEO(decodedLocation);
  }
  
  // Cerca nelle province
  const province = italianProvinces.find(p => 
    p.name.toLowerCase() === decodedLocation
  );
  if (province) {
    return seoTemplates.provinceTemplate(province.name, province.region);
  }
  
  // Cerca nelle regioni
  const italianRegionNames = [
    'abruzzo', 'basilicata', 'calabria', 'campania', 'emilia-romagna',
    'friuli-venezia giulia', 'lazio', 'liguria', 'lombardia', 'marche',
    'molise', 'piemonte', 'puglia', 'sardegna', 'sicilia', 'toscana',
    'trentino-alto adige', 'umbria', 'valle d\'aosta', 'veneto'
  ];
  
  const regionFound = italianRegionNames.find(region => 
    region === decodedLocation
  );
  if (regionFound) {
    // Capitalizza la prima lettera di ogni parola
    const regionCapitalized = regionFound.split(/[\s-]/).map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    return seoTemplates.regionTemplate(regionCapitalized);
  }
    return null;
};

export const isValidLocation = (locationName: string): boolean => {
  const decodedLocation = decodeURIComponent(locationName).toLowerCase();
  
  // Verifica se esiste nelle città principali
  const inMajorCities = majorCitiesSEO.some(city => 
    city.cityName.toLowerCase() === decodedLocation
  );
  
  if (inMajorCities) return true;
  
  // Verifica se esiste nel database delle città
  if (cityExists(decodedLocation)) return true;
  
  // Verifica se è una provincia
  const isProvince = italianProvinces.some(p => 
    p.name.toLowerCase() === decodedLocation
  );
  
  if (isProvince) return true;
  
  // Verifica se è una regione
  const italianRegionNames = [
    'abruzzo', 'basilicata', 'calabria', 'campania', 'emilia-romagna',
    'friuli-venezia giulia', 'lazio', 'liguria', 'lombardia', 'marche',
    'molise', 'piemonte', 'puglia', 'sardegna', 'sicilia', 'toscana',
    'trentino-alto adige', 'umbria', 'valle d\'aosta', 'veneto'
  ];
  
  return italianRegionNames.includes(decodedLocation);
};

// Funzione per ottenere SEO data per una città specifica dal database
export const getCitySpecificSEO = (cityName: string): LocationSEO | null => {
  const province = getProvinceByCity(cityName);
  if (!province) return null;
  
  return seoTemplates.cityTemplate(cityName, province.region, province.name);
};

// Funzione per ottenere tutte le città di una provincia per generare link SEO
export const getCitiesForProvinceSEO = (provinceName: string): string[] => {
  const province = italianProvinces.find(p => 
    p.name.toLowerCase() === provinceName.toLowerCase()
  );
  return province ? province.mainCities : [];
};

// Funzione per ottenere tutte le province di una regione per generare link SEO
export const getProvincesForRegionSEO = (regionName: string): string[] => {
  return italianProvinces
    .filter(p => p.region.toLowerCase() === regionName.toLowerCase())
    .map(p => p.name);
};

export const generateLocationUrl = (locationName: string): string => {
  return `/location/${encodeURIComponent(locationName)}`;
};

// Interfaccia per il LocationSEO con contenuti tradotti
export interface TranslatedLocationSEO {
  cityName: string;
  provinceName?: string;
  regionName: string;
  title: string;
  description: string;
  h1: string;
  content: string[];
  keywords: string[];
  locationType: 'city' | 'province' | 'region';
}

// Funzione per convertire LocationSEO con chiavi in LocationSEO con contenuti tradotti
export const translateLocationSEO = (
  locationSEO: LocationSEO, 
  t: (key: string, params?: Record<string, string>) => string
): TranslatedLocationSEO => {
  const params = {
    cityName: locationSEO.cityName,
    provinceName: locationSEO.provinceName || '',
    regionName: locationSEO.regionName
  };

  // Traduci il contenuto e dividi in paragrafi
  const translatedContent = t(locationSEO.contentKey, params);
  const contentArray = translatedContent
    .split('\n\n')
    .filter(paragraph => paragraph.trim() !== '');

  // Traduci le keywords e dividi per virgola
  const translatedKeywords = t(locationSEO.keywordsKey, params);
  const keywordsArray = translatedKeywords
    .split(',')
    .map(keyword => keyword.trim())
    .filter(keyword => keyword !== '');

  return {
    cityName: locationSEO.cityName,
    provinceName: locationSEO.provinceName,
    regionName: locationSEO.regionName,
    title: t(locationSEO.titleKey, params),
    description: t(locationSEO.descriptionKey, params),
    h1: t(locationSEO.h1Key, params),
    content: contentArray,
    keywords: keywordsArray,
    locationType: locationSEO.locationType
  };
};
